import React, { useState, useCallback, memo, useRef } from 'react';
import { useContent } from '../context/ContentContext';
import { motion, AnimatePresence, useScroll, useTransform, Variants } from 'framer-motion';
import { ChevronLeft, ChevronRight, Landmark } from 'lucide-react';

const Experience: React.FC = () => {
  const { content } = useContent();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const categories = content.experience || [];
  const activeData = categories[currentIndex];

  // 视差滚动逻辑
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const paginate = useCallback((newDirection: number) => {
    if (categories.length === 0) return;
    setDirection(newDirection);
    setCurrentIndex(prev => {
      let next = prev + newDirection;
      if (next < 0) return categories.length - 1;
      if (next >= categories.length) return 0;
      return next;
    });
  }, [categories.length]);

  const cardVariants: Variants = {
    enter: (direction: number) => ({ 
      x: direction > 0 ? 100 : -100, 
      opacity: 0,
      scale: 0.95
    }),
    center: { 
      zIndex: 1, 
      x: 0, 
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1] as [number, number, number, number]
      }
    },
    exit: (direction: number) => ({ 
      zIndex: 0, 
      x: direction < 0 ? 100 : -100, 
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.3
      }
    })
  };

  const listContainerVariants: Variants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const listItemVariants: Variants = {
    hidden: { opacity: 0, x: -10 },
    show: { opacity: 1, x: 0 }
  };

  if (categories.length === 0) return null;

  return (
    <section 
      id="experience" 
      ref={sectionRef}
      className="py-32 bg-slate-50 relative overflow-hidden"
    >
      {/* 视差背景图 */}
      <motion.div 
        style={{ y: backgroundY, opacity }}
        className="absolute inset-0 z-0 pointer-events-none"
      >
        <img 
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1920" 
          alt="Abstract Background" 
          className="w-full h-[120%] object-cover opacity-[0.03] grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-transparent to-slate-50"></div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-block px-4 py-1 bg-slate-900 text-[#D4AF37] rounded-full text-[10px] font-black uppercase tracking-[0.4em] mb-4 shadow-lg shadow-slate-900/10">
            Track Record
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">服务案例与项目履历</h2>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto mt-6 rounded-full"></div>
        </motion.div>

        {/* 分类按钮组 - 增加交错入场 */}
        <motion.div 
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={{
            hidden: { opacity: 0 },
            show: {
              opacity: 1,
              transition: { staggerChildren: 0.1 }
            }
          }}
          className="flex flex-wrap justify-center gap-4 mb-16"
        >
          {categories.map((cat, idx) => (
            <motion.button
              key={cat.id || idx}
              variants={{
                hidden: { opacity: 0, y: 10 },
                show: { opacity: 1, y: 0 }
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => { setDirection(idx > currentIndex ? 1 : -1); setCurrentIndex(idx); }}
              className={`px-8 py-3.5 rounded-2xl text-sm font-bold transition-all relative overflow-hidden ${
                idx === currentIndex 
                ? 'bg-slate-900 text-[#D4AF37] shadow-[0_15px_30px_rgba(15,23,42,0.15)] ring-2 ring-[#D4AF37]/20' 
                : 'bg-white text-slate-500 hover:bg-slate-100 shadow-sm'
              }`}
            >
              {cat.title}
              {idx === currentIndex && (
                <motion.div 
                  layoutId="activePill"
                  className="absolute inset-0 bg-[#D4AF37]/5 pointer-events-none"
                />
              )}
            </motion.button>
          ))}
        </motion.div>

        <div className="relative">
          {/* 背景装饰性圆环 */}
          <div className="absolute -top-12 -right-12 w-64 h-64 border-[32px] border-slate-200/30 rounded-full -z-10 pointer-events-none"></div>
          <div className="absolute -bottom-12 -left-12 w-48 h-48 border-[24px] border-[#D4AF37]/5 rounded-full -z-10 pointer-events-none"></div>

          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={cardVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="bg-white rounded-[3rem] p-8 md:p-20 shadow-[0_40px_100px_rgba(0,0,0,0.06)] border border-slate-100 overflow-hidden relative"
            >
              {/* 卡片装饰线条 */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-slate-50 to-transparent"></div>
              
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 relative z-10">
                <div className="lg:col-span-5 flex flex-col justify-center">
                   <motion.div
                     initial={{ opacity: 0, x: -20 }}
                     animate={{ opacity: 1, x: 0 }}
                     transition={{ delay: 0.2 }}
                   >
                     {activeData && (
                       <>
                         <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                           {activeData.title}
                         </h3>
                         <p className="text-slate-500 text-lg leading-relaxed mb-10 border-l-4 border-[#D4AF37]/30 pl-6">
                           {activeData.description || "深耕各行各业财税实践，积累了丰富的执业经验与标杆案例。"}
                         </p>
                       </>
                     )}
                     
                     <div className="flex gap-4">
                       <button 
                        onClick={() => paginate(-1)} 
                        className="p-4 bg-slate-50 rounded-2xl hover:bg-[#D4AF37] hover:text-white transition-all duration-300 shadow-sm group"
                       >
                         <ChevronLeft size={24} className="group-hover:-translate-x-1 transition-transform" />
                       </button>
                       <button 
                        onClick={() => paginate(1)} 
                        className="p-4 bg-slate-50 rounded-2xl hover:bg-[#D4AF37] hover:text-white transition-all duration-300 shadow-sm group"
                       >
                         <ChevronRight size={24} className="group-hover:translate-x-1 transition-transform" />
                       </button>
                     </div>
                   </motion.div>
                </div>

                <div className="lg:col-span-7">
                  <motion.div 
                    variants={listContainerVariants}
                    initial="hidden"
                    animate="show"
                    className="grid grid-cols-1 md:grid-cols-2 gap-10"
                  >
                    {activeData?.content.map((group, gIdx) => (
                      <div key={gIdx} className="space-y-6">
                        <div className="flex items-center gap-3 text-slate-900 font-bold">
                          <div className="p-2 bg-slate-900 text-[#D4AF37] rounded-lg">
                            <Landmark size={18} />
                          </div>
                          <span className="text-sm uppercase tracking-widest">{group.title}</span>
                        </div>
                        <ul className="space-y-4">
                          {group.list.map((item, i) => (
                            <motion.li 
                              key={i} 
                              variants={listItemVariants}
                              className="text-slate-600 flex items-start gap-3 group/item cursor-default"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2.5 shrink-0 group-hover/item:scale-150 transition-transform duration-300" />
                              <span className="group-hover/item:text-slate-950 transition-colors duration-300 font-medium">
                                {item}
                              </span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default memo(Experience);